(() => {
  if (window.__byzaynSocialBagNativeFix) return;
  window.__byzaynSocialBagNativeFix = true;

  const cache = new Map();
  let activeCard = null;
  let activeTrigger = null;

  const dialog = () => document.getElementById('BzSocialBagDialog');
  const status = () => document.getElementById('BzSocialBagStatus');

  const showStatus = (message, isError = false) => {
    const node = status();
    if (!node) return;
    clearTimeout(window.__byzaynSocialBagNativeStatusTimer);
    node.textContent = message;
    node.classList.toggle('is-error', isError);
    node.hidden = false;
    window.__byzaynSocialBagNativeStatusTimer = setTimeout(() => { node.hidden = true; }, 2400);
  };

  const setLoading = (button, loading) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = loading;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
  };

  const setAdded = (button, card) => {
    if (!(button instanceof HTMLButtonElement)) return;
    const fallback = button.dataset.defaultLabel || 'Add to bag';
    const title = card?.dataset.productTitle || 'Item';
    button.classList.add('is-added');
    button.setAttribute('aria-label', `${title} added to bag`);
    setTimeout(() => {
      button.classList.remove('is-added');
      button.setAttribute('aria-label', fallback);
    }, 1200);
  };

  const getProduct = async (card) => {
    const raw = card?.dataset.productUrl;
    if (!raw) throw new Error('Product link unavailable.');
    const url = new URL(raw, location.origin);
    const key = url.pathname;
    if (cache.has(key)) return cache.get(key);
    const response = await fetch(`${url.pathname.replace(/\.js$/, '')}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Product options could not be loaded.');
    const product = await response.json();
    cache.set(key, product);
    return product;
  };

  const variantGid = (id) => {
    const value = String(id || '').trim();
    if (!value) throw new Error('Product option unavailable.');
    return value.startsWith('gid://shopify/ProductVariant/')
      ? value
      : `gid://shopify/ProductVariant/${value}`;
  };

  const getActions = async () => {
    if (window.Shopify?.actions?.updateCart) return window.Shopify.actions;
    if (document.readyState === 'loading') {
      await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
    }
    if (!window.Shopify?.actions?.updateCart) {
      throw new Error('Bag is temporarily unavailable. Please try again.');
    }
    return window.Shopify.actions;
  };

  const addVariant = async (variantId, trigger, card) => {
    setLoading(trigger, true);
    try {
      const actions = await getActions();
      const result = await actions.updateCart(
        { lines: [{ merchandiseId: variantGid(variantId), quantity: 1 }] },
        {
          event: {
            context: 'product',
            detail: { source: 'byzayn-social-feed', productId: card?.dataset.productId || '' }
          }
        }
      );
      if (result?.userErrors?.length) {
        throw new Error(result.userErrors[0]?.message || 'Could not add this item to your bag.');
      }
      setAdded(trigger, card);
      showStatus(result?.warnings?.[0]?.message || 'Added to bag');
      return true;
    } catch (error) {
      showStatus(error instanceof Error ? error.message : 'Could not add this item to your bag.', true);
      return false;
    } finally {
      setLoading(trigger, false);
    }
  };

  const openPicker = (product, card, trigger) => {
    const modal = dialog();
    const heading = modal?.querySelector('[data-bz-bag-heading]');
    const productName = modal?.querySelector('[data-bz-bag-product-name]');
    const options = modal?.querySelector('[data-bz-bag-options]');
    const productLink = modal?.querySelector('[data-bz-bag-product-link]');
    if (!modal || !heading || !productName || !options || !productLink) {
      throw new Error('Product options are temporarily unavailable.');
    }

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const names = (Array.isArray(product?.options) ? product.options : [])
      .map((option) => typeof option === 'string' ? option : option?.name)
      .filter(Boolean);
    const sizeOnly = names.length === 1 && /size/i.test(names[0]);

    activeCard = card;
    activeTrigger = trigger;
    heading.textContent = sizeOnly ? 'Choose your size' : 'Choose an option';
    productName.textContent = product?.title || card?.dataset.productTitle || '';
    productLink.href = card?.dataset.productUrl || '#';
    options.replaceChildren();
    options.classList.toggle('is-size-only', sizeOnly);

    variants.forEach((variant) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bz-social-bag-option';
      button.dataset.bzNativeVariant = String(variant.id);
      button.textContent = variant.title === 'Default Title' ? 'Add to bag' : variant.title;
      button.disabled = variant.available === false;
      if (variant.available === false) {
        button.title = 'Sold out';
        button.setAttribute('aria-label', `${button.textContent}, sold out`);
      }
      options.append(button);
    });

    if (typeof modal.showModal === 'function') modal.showModal();
    else modal.setAttribute('open', '');
  };

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const bag = target.closest('[data-bz-social-bag]');
    if (bag instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      if (bag.disabled) return;
      const card = bag.closest('[data-social-card]');
      if (!card) return;
      setLoading(bag, true);
      try {
        const product = await getProduct(card);
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const available = variants.filter((variant) => variant.available !== false);
        if (!available.length) {
          showStatus('Sold out', true);
          return;
        }
        if (variants.length === 1) {
          setLoading(bag, false);
          await addVariant(variants[0].id, bag, card);
        } else {
          setLoading(bag, false);
          openPicker(product, card, bag);
        }
      } catch (error) {
        showStatus(error instanceof Error ? error.message : 'Could not open product options.', true);
      } finally {
        setLoading(bag, false);
      }
      return;
    }

    const choice = target.closest('[data-bz-native-variant]');
    if (choice instanceof HTMLButtonElement && activeCard && activeTrigger) {
      event.preventDefault();
      event.stopPropagation();
      if (choice.disabled) return;
      const modal = dialog();
      const choices = [...(modal?.querySelectorAll('[data-bz-native-variant]') || [])];
      choices.forEach((button) => { button.disabled = true; });
      const ok = await addVariant(choice.dataset.bzNativeVariant, activeTrigger, activeCard);
      if (ok) {
        if (modal?.open) modal.close();
      } else {
        choices.forEach((button) => { if (!button.title) button.disabled = false; });
      }
      return;
    }
  }, true);

  document.addEventListener('close', (event) => {
    if (event.target?.id !== 'BzSocialBagDialog') return;
    activeCard = null;
    activeTrigger = null;
  }, true);
})();
